// Utils
import { TransactionItem } from '../transactions/transactionsTypes';
import type { PlannedSpendingCardData, PlannedSpendingItem } from './freeToSpendTypes';

export const getPlannedSpendingCardData = (
  plannedItem: PlannedSpendingItem,
  isPreviousMonth: boolean,
): PlannedSpendingCardData => {
  const target = (plannedItem.overwrittenTargetAmount ?? plannedItem.targetAmount) || 0;
  const calculatedSpentAmount = plannedItem.calculatedSpentAmount || 0;
  const leftToSpend = target + calculatedSpentAmount;
  const overSpent = leftToSpend < 0;
  const showUnused = isPreviousMonth && Math.abs(calculatedSpentAmount) < Math.abs(target);
  return { ...plannedItem, target, calculatedSpentAmount, leftToSpend, overSpent, showUnused };
};

export const getWidthPercent = (spent: number, plannedSpend: number, rollover: number) => {
  let widthPercent = 0;
  let widthPercentText = 0;
  let rolloverPercent = 0;
  let leftPercent = 0;
  let originalRolloverPercent = 0;

  const safeSpent = !Number.isNaN(spent) ? spent : 0;
  const safePlannedSpend = !Number.isNaN(plannedSpend) ? plannedSpend : 0;
  const safeRollover = !Number.isNaN(rollover) ? rollover : 0;

  if (safeRollover > 0) {
    // Calculate spent %
    if (safeSpent > 0 && Math.abs(safeSpent) < Math.abs(safePlannedSpend) + safeRollover) {
      widthPercent = (Math.abs(safeSpent) / (Math.abs(safePlannedSpend) + safeRollover)) * 100;
    }
    if (Math.abs(safeSpent) >= Math.abs(plannedSpend) + safeRollover) {
      widthPercent = 100;
    }

    widthPercentText = (Math.abs(safeSpent) / (Math.abs(safePlannedSpend) + safeRollover)) * 100;

    if (safeSpent <= 0) {
      widthPercentText = 0;
      widthPercent = 0;
    }

    // Calculate rollover %
    rolloverPercent = (Math.abs(safeRollover) / (safePlannedSpend + Math.abs(safeRollover))) * 100;
    originalRolloverPercent = rolloverPercent;
    if (100 - rolloverPercent - widthPercent <= 0) {
      rolloverPercent = 100 - widthPercent;
    }

    // Calculate Left %
    leftPercent = 100 - rolloverPercent - widthPercent;
  } else if (safeRollover < 0) {
    // Calculate rollover %
    rolloverPercent = (Math.abs(safeRollover) / safePlannedSpend) * 100;
    originalRolloverPercent = rolloverPercent;

    // Calculate spent %
    if (safeSpent > 0 && Math.abs(safeSpent) + Math.abs(safeRollover) < Math.abs(safePlannedSpend)) {
      widthPercent = (Math.abs(safeSpent) / Math.abs(safePlannedSpend)) * 100;
    }
    if (Math.abs(safeSpent) + Math.abs(safeRollover) >= Math.abs(plannedSpend)) {
      widthPercent = 100 - rolloverPercent; // Can become negative
    }

    widthPercentText = ((Math.abs(safeSpent) + Math.abs(safeRollover)) / Math.abs(safePlannedSpend)) * 100;

    if (safeSpent <= 0) {
      // We want to show the text for what it is if rollovers fill up the entire bar!
      if (rolloverPercent < 100) {
        widthPercentText = 0;
      }
      widthPercent = 0;
    }
    if (widthPercent < 0) {
      widthPercent = 0;
    }

    // Calculate Left %
    leftPercent = 100 - rolloverPercent - widthPercent;
  } else {
    if (safeSpent > 0 && Math.abs(safeSpent) < Math.abs(safePlannedSpend)) {
      widthPercent = (Math.abs(safeSpent) / Math.abs(safePlannedSpend)) * 100;
    }
    if (Math.abs(safeSpent) >= Math.abs(plannedSpend)) {
      widthPercent = 100;
    }
    if (safeSpent < 0) {
      widthPercent = 0;
    }
    widthPercentText = (Math.abs(safeSpent) / Math.abs(safePlannedSpend)) * 100;
    leftPercent = 100 - widthPercent;
    rolloverPercent = 0;
  }

  return {
    originalRolloverPercent,
    leftPercent,
    widthPercent,
    // We want to show the text if the negative rollover fills up the bar completely!
    widthPercentText: widthPercent > 7.9 || (rolloverPercent >= 100 && safeRollover < 0) ? widthPercentText : null,
    rolloverPercent,
  };
};

export const getAllMatchedTransactionArrays = (transferTransactionsItems: TransactionItem[]) => {
  const retObj: Record<string, TransactionItem[]> = {};
  const matchedArrayIds: string[] = [];
  transferTransactionsItems.forEach((txnItem) => {
    if (Object.values(retObj).length + matchedArrayIds.length !== transferTransactionsItems.length) {
      // check if we have paired all txns and no longer need to search
      const transactionItem = txnItem.item;
      const transactionItemId = transactionItem.id;
      if (transactionItemId && !matchedArrayIds.includes(transactionItemId)) {
        if (!(transactionItemId in retObj)) {
          retObj[transactionItemId] = [];
        }
        retObj[transactionItemId].push(txnItem);
        const otherSideOfTransfer = transferTransactionsItems.find(
          (transferTxn) =>
            (transferTxn.item.transfer?.id === transactionItemId ||
              transferTxn.item.id === transactionItem.transfer?.id) &&
            transactionItem.coa?.id === transferTxn.item.accountId &&
            transferTxn.item.amount === transactionItem.amount * -1 &&
            transactionItem.postedOn === transferTxn.item.postedOn,
        );
        if (otherSideOfTransfer && otherSideOfTransfer.item.id && !(otherSideOfTransfer.item.id in retObj)) {
          if (otherSideOfTransfer.item.amount > 0) {
            retObj[transactionItemId].push(otherSideOfTransfer);
          } else {
            retObj[transactionItemId].unshift(otherSideOfTransfer);
          }
          matchedArrayIds.push(otherSideOfTransfer.item.id);
        }
      }
    }
  });
  return Object.values(retObj);
};
